import { useState, useCallback } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { convertCurrency, DEFAULT_EXCHANGE_RATES, CURRENCY } from '../utils/currencyUtils';

/**
 * Custom hook to manage payment currency disclaimer modal
 * 
 * @param {Function} onConfirm - Function to call when the user proceeds with payment
 * @param {number} exchangeRate - The current USD to LKR exchange rate
 * @returns {Object} - The hook methods and state
 */
export const usePaymentCurrencyDisclaimer = (
    onConfirm: () => void,
    exchangeRate: number = DEFAULT_EXCHANGE_RATES[CURRENCY.LKR]
) => {
    // Modal state
    const [opened, { open, close }] = useDisclosure(false);
    const [amountUSD, setAmountUSD] = useState(0);

    // Calculate LKR amount based on exchange rate
    const amountLKR = convertCurrency(amountUSD, exchangeRate);

    // Handle proceed action
    const handleProceed = useCallback(() => {
        close();
        onConfirm();
    }, [close, onConfirm]);

    // Function to show the disclaimer with a specific amount
    const showDisclaimer = useCallback((amount: number) => {
        setAmountUSD(amount);
        open();
    }, [open]);

    return {
        opened,
        close,
        showDisclaimer,
        handleProceed,
        amountUSD,
        amountLKR,
        exchangeRate
    };
};

export default usePaymentCurrencyDisclaimer;
